import { AspectRatio } from "@/components/ui/aspect-ratio";
import WebinarChapters, { type Chapter } from "./WebinarChapters";
import { useLanguage } from "@/hooks/useLanguage";

interface WebinarPart {
  id: string;
  eyebrow: string;
  title: string;
  paragraphs: string[]; // may contain <b>...</b> for bold
  recordingUrl: string;
  chapters: Chapter[];
}

const CONTENT: Record<"en" | "es" | "zh-cn", WebinarPart[]> = {
  en: [
    {
      id: "webinar1",
      eyebrow: "Webinar 1",
      title: "Navigating Post-Market Compliance Under MDR",
      paragraphs: [
        "Achieving CE marking is just the beginning. This webinar will clarify how Post-Market Surveillance (PMS) and Post-Market Clinical Follow-up (PMCF) requirements shift once your medical device is on the market.",
        "You'll get practical guidance on staying compliant under EU MDR—especially for legacy devices already in use.",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-navigating-post-market-compliance-under-mdr.mp4",
      chapters: [
        { time: "00:00", title: "Introduction" },
        { time: "03:50", title: "PMS after EU market approval" },
        { time: "18:30", title: "PMS versus PMCF" },
        { time: "21:44", title: "Data sources" },
        { time: "29:17", title: "PMCF challenges & solutions" },
        { time: "36:22", title: "Q&A" },
        { time: "51:04", title: "Key takeaways and closing remarks" },
      ],
    },
    {
      id: "webinar2",
      eyebrow: "Webinar 2",
      title: "PMCF in Practice – Strategies, Tools & Expert Insights",
      paragraphs: [
        "This session dives into the practical side of PMCF. Our team shares how we approach PMCF activities such as clinical investigations, literature reviews, usability studies, and surveys.",
        "<b>A notified body expert will provide real audit insights and share what they expect to see in your technical documentation.</b>",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-pmcf-in-practice-strategies-tools-expert-insights.mp4",
      chapters: [
        { time: "00:00", title: "Introduction" },
        { time: "04:55", title: "Literature Research" },
        { time: "15:20", title: "PMCF Studies" },
        { time: "27:25", title: "PMCF Surveys" },
        { time: "37:50", title: "PMCF in Practice – A Notified Body Perspective" },
        { time: "45:55", title: "Q&A" },
        { time: "56:25", title: "Key takeaways and closing remarks" },
      ],
    },
  ],
  es: [
    {
      id: "webinar1",
      eyebrow: "Webinar 1",
      title: "Cumplimiento poscomercialización según el MDR",
      paragraphs: [
        "Obtener el marcado CE es solo el principio. Este webinar aclara cómo cambian los requisitos de Vigilancia Poscomercialización (PMS) y Seguimiento Clínico Poscomercialización (PMCF) una vez que tu dispositivo médico está en el mercado.",
        "Recibirás orientación práctica para mantener el cumplimiento bajo el MDR de la UE, especialmente para productos heredados ya en uso.",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-navigating-post-market-compliance-under-mdr.mp4",
      chapters: [
        { time: "00:00", title: "Introduction", title_es: "Introducción" },
        { time: "03:50", title: "PMS after EU market approval", title_es: "PMS tras la aprobación en el mercado de la UE" },
        { time: "18:30", title: "PMS versus PMCF", title_es: "PMS frente a PMCF" },
        { time: "21:44", title: "Data sources", title_es: "Fuentes de datos" },
        { time: "29:17", title: "PMCF challenges & solutions", title_es: "Retos y soluciones del PMCF" },
        { time: "36:22", title: "Q&A", title_es: "Preguntas y respuestas" },
        { time: "51:04", title: "Key takeaways and closing remarks", title_es: "Conclusiones y cierre" },
      ],
    },
    {
      id: "webinar2",
      eyebrow: "Webinar 2",
      title: "PMCF en la práctica: estrategias, herramientas e insights de expertos",
      paragraphs: [
        "Esta sesión profundiza en el lado práctico del PMCF. Nuestro equipo explica cómo abordamos actividades de PMCF como investigaciones clínicas, revisiones bibliográficas, estudios de usabilidad y encuestas.",
        "<b>Un experto de un organismo notificado aportará información real de auditorías y compartirá qué esperan ver en tu documentación técnica.</b>",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-pmcf-in-practice-strategies-tools-expert-insights.mp4",
      chapters: [
        { time: "00:00", title: "Introduction", title_es: "Introducción" },
        { time: "04:55", title: "Literature Research", title_es: "Investigación bibliográfica" },
        { time: "15:20", title: "PMCF Studies", title_es: "Estudios de PMCF" },
        { time: "27:25", title: "PMCF Surveys", title_es: "Encuestas de PMCF" },
        { time: "37:50", title: "PMCF in Practice – A Notified Body Perspective", title_es: "PMCF en la práctica: perspectiva de un organismo notificado" },
        { time: "45:55", title: "Q&A", title_es: "Preguntas y respuestas" },
        { time: "56:25", title: "Key takeaways and closing remarks", title_es: "Conclusiones y cierre" },
      ],
    },
  ],
  "zh-cn": [
    {
      id: "webinar1",
      eyebrow: "网络研讨会 1",
      title: "在欧盟 MDR 下应对上市后合规",
      paragraphs: [
        "获得 CE 标志只是起点。本次网络研讨会将阐明医疗器械上市后，上市后监测（PMS）和上市后临床跟踪（PMCF）要求的变化。",
        "您将获得关于在欧盟 MDR 下保持合规的实用指导，尤其针对仍在使用的既有器械。",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-navigating-post-market-compliance-under-mdr.mp4",
      chapters: [
        { time: "00:00", title: "Introduction" },
        { time: "03:50", title: "PMS after EU market approval" },
        { time: "18:30", title: "PMS versus PMCF" },
        { time: "21:44", title: "Data sources" },
        { time: "29:17", title: "PMCF challenges & solutions" },
        { time: "36:22", title: "Q&A" },
        { time: "51:04", title: "Key takeaways and closing remarks" },
      ],
    },
    {
      id: "webinar2",
      eyebrow: "网络研讨会 2",
      title: "PMCF 实务：策略、工具与专家洞察",
      paragraphs: [
        "本次会议深入探讨 PMCF 的实操层面。我们的团队将分享如何开展 PMCF 活动，包括临床研究、文献综述、可用性研究和问卷调查。",
        "<b>公告机构专家将分享真实的审计洞察，以及他们期望在技术文档中看到的内容。</b>",
      ],
      recordingUrl:
        "https://info.qbdgroup.com/hubfs/Website%202025%20-%20Webinar%20uploads/qbdgroup-pmcf-in-practice-strategies-tools-expert-insights.mp4",
      chapters: [
        { time: "00:00", title: "Introduction" },
        { time: "04:55", title: "Literature Research" },
        { time: "15:20", title: "PMCF Studies" },
        { time: "27:25", title: "PMCF Surveys" },
        { time: "37:50", title: "PMCF in Practice – A Notified Body Perspective" },
        { time: "45:55", title: "Q&A" },
        { time: "56:25", title: "Key takeaways and closing remarks" },
      ],
    },
  ],
};

const TwoWebinarBlock = () => {
  const lang = useLanguage();
  const parts = CONTENT[lang] || CONTENT.en;

  return (
    <>
      {parts.map((part, idx) => {
        const isBlue = idx === 1;
        const wrapperBg = isBlue ? "bg-[#f1f7ff]" : "bg-background";
        const eyebrowColor = isBlue ? "text-accent" : "text-accent";
        const titleColor = isBlue ? "text-foreground" : "text-foreground";
        const bodyColor = isBlue ? "text-muted-foreground" : "text-muted-foreground";
        const videoBg = isBlue ? "bg-[#f1f7ff]" : "bg-primary";
        const chapterChromeClass = isBlue
          ? "[&_section]:bg-transparent [&_h2]:text-foreground [&_ol]:border-border/60 [&_p.text-xs.font-bold]:text-accent [&_li:hover]:bg-secondary/40 [&_span.text-xs.font-mono]:text-muted-foreground [&_span.text-sm.font-medium]:text-foreground [&_span.bg-border]:bg-border [&_span.bg-border]:group-hover:bg-accent"
          : "";

        return (
          <div key={part.id} id={part.id} className={wrapperBg}>
            <section className="pt-16 sm:pt-20 pb-4">
              <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
                <p className={`text-xs font-bold uppercase tracking-wider ${eyebrowColor} mb-2`}>
                  {part.eyebrow}
                </p>
                <h2 className={`text-2xl sm:text-3xl font-bold ${titleColor} mb-4`}>
                  {part.title}
                </h2>
                {part.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={`text-base ${bodyColor} leading-relaxed mb-3 last:mb-0`}
                    dangerouslySetInnerHTML={{ __html: p }}
                  />
                ))}
              </div>
            </section>

            <section className="py-8 sm:py-10">
              <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
                <AspectRatio ratio={16 / 9} className={`rounded-2xl overflow-hidden shadow-2xl ${videoBg}`}>
                  <video
                    src={part.recordingUrl}
                    controls
                    className="w-full h-full object-contain"
                    preload="metadata"
                  />
                </AspectRatio>
              </div>
            </section>

            <div className={`pb-16 sm:pb-20 ${chapterChromeClass}`}>
              <WebinarChapters chapters={part.chapters} />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default TwoWebinarBlock;
